import { useEffect, type RefObject } from "react";

interface UseWidthObserverProps {
  ref: RefObject<HTMLElement | null>;
  onWidthChange: (width: number) => void;
  debounceMs?: number;
}

/**
 * Reports the observed element's content-box width whenever it changes,
 * debounced and coalesced into an animation frame. A dependency-free port of
 * apps/web's useWidthObserver, which relies on lodash.
 */
export function useWidthObserver({
  ref,
  onWidthChange,
  debounceMs = 100,
}: UseWidthObserverProps) {
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    let prevWidth: number | null = null;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let frame = 0;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (prevWidth === width) return;
        prevWidth = width;
        frame = requestAnimationFrame(() => onWidthChange(width));
      }, debounceMs);
    });

    observer.observe(element);
    return () => {
      observer.disconnect();
      clearTimeout(timeout);
      cancelAnimationFrame(frame);
    };
  }, [ref, onWidthChange, debounceMs]);
}
